export type ChatThemeCategory = "free" | "premium" | "animated" | "seasonal";

export type ChatThemeEffect =
  | "none"
  | "stars"
  | "neon"
  | "aurora"
  | "sparkle"
  | "snow"
  | "hearts"
  | "bubbles"
  | "sakura"
  | "fireflies"
  | "ghosts"
  | "confetti"
  | "sun"
  | "goldOrbs";

export interface ChatTheme {
  id: string;
  name: string;
  category: ChatThemeCategory;
  price: number; // 0 = free / default
  /** CSS background applied to chat scroll area */
  background: string;
  /** Tint of message bubbles (optional, used as overlay) */
  bubbleTint?: string;
  /** Animated overlay effect */
  effect: ChatThemeEffect;
  /** Short description shown in shop card */
  tagline: string;
  /** Accent hex used for previews and glows */
  accent: string;
}

export const CHAT_THEMES: ChatTheme[] = [
  // ---------- FREE ----------
  {
    id: "midnight",
    name: "Midnight",
    category: "free",
    price: 0,
    background: "linear-gradient(180deg, hsl(240 25% 6%) 0%, hsl(250 30% 9%) 100%)",
    effect: "none",
    tagline: "Default · clean & dark",
    accent: "#7c5cff",
  },
  {
    id: "graphite",
    name: "Graphite",
    category: "free",
    price: 0,
    background: "linear-gradient(180deg, #4a4a52 0%, #2e2e35 50%, #1f1f24 100%)",
    effect: "none",
    tagline: "Smooth charcoal grey",
    accent: "#9aa0a6",
  },
  // ---------- PREMIUM (static) ----------
  {
    id: "ocean",
    name: "Ocean Deep",
    category: "premium",
    price: 150,
    background: "linear-gradient(180deg, #0c2340 0%, #1a4a6e 60%, #2d8a9e 100%)",
    effect: "bubbles",
    tagline: "Flowing deep-sea blues",
    accent: "#5cbdb9",
  },
  {
    id: "sunset",
    name: "Sunset Blaze",
    category: "premium",
    price: 200,
    background: "linear-gradient(180deg, #ff6b35 0%, #e84393 50%, #6c5ce7 100%)",
    effect: "sun",
    tagline: "Warm gradient glow",
    accent: "#ff6b35",
  },
  {
    id: "forest",
    name: "Forest Mist",
    category: "premium",
    price: 180,
    background: "linear-gradient(180deg, #1a3c2a 0%, #2d5a3d 60%, #5a8a5c 100%)",
    effect: "fireflies",
    tagline: "Calm forest at dusk",
    accent: "#a0c49d",
  },
  {
    id: "noir-gold",
    name: "Noir & Gold",
    category: "premium",
    price: 250,
    background: "linear-gradient(180deg, #0d0d0d 0%, #1a1a1a 100%)",
    bubbleTint: "rgba(201,168,76,0.08)",
    effect: "goldOrbs",
    tagline: "Luxury black & gold",
    accent: "#c9a84c",
  },
  // ---------- ANIMATED ----------
  {
    id: "neon-city",
    name: "Neon City",
    category: "animated",
    price: 400,
    background:
      "linear-gradient(180deg, #0a0a1a 0%, #16213e 60%, #0f3460 100%)",
    effect: "neon",
    tagline: "Cyberpunk glow grid",
    accent: "#e94560",
  },
  {
    id: "aurora",
    name: "Aurora",
    category: "animated",
    price: 500,
    background:
      "linear-gradient(180deg, #0a1a2a 0%, #0d3b3b 50%, #1a4a6e 100%)",
    effect: "aurora",
    tagline: "Northern lights flow",
    accent: "#4ade80",
  },
  // ---------- SEASONAL ----------
  {
    id: "sakura",
    name: "Sakura",
    category: "seasonal",
    price: 320,
    background:
      "linear-gradient(180deg, #2a0a1a 0%, #5a1a2e 60%, #f8c8d8 100%)",
    effect: "sakura",
    tagline: "Cherry blossom drift",
    accent: "#f8c8d8",
  },
  {
    id: "frostbite",
    name: "Frostbite",
    category: "seasonal",
    price: 260,
    background:
      "linear-gradient(180deg, #0a1f3a 0%, #1e4a7a 50%, #7ec8e3 100%)",
    effect: "snow",
    tagline: "Falling winter snow",
    accent: "#bde0fe",
  },
  {
    id: "haunted",
    name: "Haunted",
    category: "animated",
    price: 380,
    background:
      "linear-gradient(180deg, #0a0a14 0%, #1a0d2e 60%, #2d1b4e 100%)",
    effect: "ghosts",
    tagline: "Spooky floating ghosts",
    accent: "#e2d6ff",
  },
  {
    id: "lovestruck",
    name: "Lovestruck",
    category: "animated",
    price: 320,
    background:
      "linear-gradient(180deg, #3a0a1f 0%, #6e1a3a 60%, #ff6b9d 100%)",
    effect: "hearts",
    tagline: "Hearts popping everywhere",
    accent: "#ff8fb3",
  },
  {
    id: "galaxy",
    name: "Galaxy",
    category: "animated",
    price: 420,
    background:
      "#000000",
    effect: "stars",
    tagline: "Pure black starfield",
    accent: "#a78bfa",
  },
];

export const DEFAULT_THEME_ID = "midnight";
export const FREE_THEME_IDS = CHAT_THEMES.filter((t) => t.price === 0).map((t) => t.id);

export const getTheme = (id: string | null | undefined): ChatTheme =>
  CHAT_THEMES.find((t) => t.id === id) ?? CHAT_THEMES[0];